import { Suspense, useEffect } from 'react';
import { Canvas } from '@react-three/fiber';
import {
  EffectComposer,
  Bloom,
  Vignette,
  ToneMapping,
  SMAA,
} from '@react-three/postprocessing';
import { ToneMappingMode } from 'postprocessing';
import * as THREE from 'three';
import { useGameStore } from '../../core/store';
import {
  unlockAudio,
  preloadAudio,
  startIntroBed,
} from '../audio';
import { useIsMobile } from '../../core/useIsMobile';
import { LockerRoomWorld } from './LockerRoomWorld';
import { LockerCamera } from './LockerCamera';
import { LockerClaimBeat } from './LockerClaimBeat';
import { LockerIdentityUI } from './LockerIdentityUI';
import { LockerIdentityMobile } from './LockerIdentityMobile';
import { FullscreenCanvasSync } from '../ui/FullscreenCanvasSync';
import './locker.css';
import './pro-jersey.css';

/**
 * Light FX — no DOF / no claim flash bloom (dissolve stays matte).
 * Mobile keeps SMAA + gentle bloom so the jersey close-up stays crisp.
 */
function LockerFX({ mobile = false }: { mobile?: boolean }) {
  return (
    <EffectComposer multisampling={mobile ? 0 : 2}>
      <SMAA />
      <Bloom
        intensity={mobile ? 0.05 : 0.07}
        luminanceThreshold={mobile ? 0.9 : 0.94}
        luminanceSmoothing={0.35}
        mipmapBlur
      />
      <Vignette eskil={false} offset={0.28} darkness={mobile ? 0.28 : 0.4} />
      <ToneMapping mode={ToneMappingMode.ACES_FILMIC} />
    </EffectComposer>
  );
}

/** One cinematic canvas + glass identity panel. No floating duplicate jersey. */
export function LockerExperience() {
  const mobile = useIsMobile();
  const quality = useGameStore((s) => s.quality);
  const setOverlayOpacity = useGameStore((s) => s.setOverlayOpacity);

  useEffect(() => {
    setOverlayOpacity(0);
    const id = window.setTimeout(() => setOverlayOpacity(0), 300);

    void unlockAudio()
      .then(() => preloadAudio())
      .then(() => {
        void startIntroBed();
      });

    return () => window.clearTimeout(id);
  }, [setOverlayOpacity]);

  const dprMax = Array.isArray(quality.dpr) ? quality.dpr[1] : 1.5;
  // Locker is a hero close-up — don't inherit the global mobile 1.15 DPR cap
  const deviceDpr =
    typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
  const lockerDpr: [number, number] = mobile
    ? [1, Math.min(Math.max(deviceDpr, 1.75), 2.25)]
    : [1, Math.min(dprMax ?? 1.75, 2)];

  return (
    <div className="locker-root">
      <div className="locker-canvas">
        <Canvas
          dpr={lockerDpr}
          frameloop="always"
          style={{ width: '100%', height: '100%', display: 'block' }}
          shadows
          camera={{
            position: [0, 1.65, 5.5],
            fov: mobile ? 48 : 42,
            near: 0.1,
            far: 40,
          }}
          gl={{
            antialias: true,
            powerPreference: 'high-performance',
            alpha: false,
            stencil: false,
            depth: true,
          }}
          onCreated={({ gl }) => {
            gl.setClearColor('#06060a');
            gl.outputColorSpace = THREE.SRGBColorSpace;
            gl.toneMapping = THREE.NoToneMapping;
            gl.localClippingEnabled = true;
            gl.shadowMap.enabled = true;
            gl.shadowMap.type = THREE.PCFSoftShadowMap;
          }}
        >
          <Suspense fallback={null}>
            <FullscreenCanvasSync />
            <LockerRoomWorld />
            <LockerCamera />
            <LockerClaimBeat />
            <LockerFX mobile={mobile} />
          </Suspense>
        </Canvas>
      </div>

      {mobile ? <LockerIdentityMobile /> : <LockerIdentityUI />}
    </div>
  );
}
